import { useRef, useState } from 'react';
import { Box, Button, InputAdornment, OutlinedInput, Typography, Snackbar, Alert } from '@mui/material';
import { useQuery } from 'react-query';
import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import useSalaryPage from 'app/pages/SalaryPage/useSalaryPage';
import DownloadAdocIcon from 'app/components/Icons/DownloadAdocIcon';
import FileUploadIcon from 'app/components/Icons/FileUploadIcon';
import { salaryService, useGetSalaryWorkers, useSendSalaryApply } from 'app/utils/services/salaryServices';
import { grayFilter } from 'app/components/ThemeProvider/colors';
import CustomSelect from 'app/components/CustomSelect';
import CustomInput from 'app/components/CustomInput';
import CalendarIcon from 'app/components/Icons/CalendarIcon';
import PageHeader from 'app/components/PageHeader';
import SearchInput from 'app/components/SearchInput';
import { ConfirmPaymentFormData, SalaryApplyForm } from 'app/utils/services/types/salary.type';
import useCurrencySymbol from 'app/utils/customHooks/useCurrencySymbol';

import EmployeeList from './EmployeeList';
import UnEmployeeList from './UnEmployeeList';
import PaymentsHistory from './PaymentsHistory';
import PaymentDetails from './PaymentDetails';
import SalaryTable from './SalaryTable';
import CheckModal from './CheckModal';

import { btnStatusPayment, inputsBox, selectDetailsBox, greenBtn, inputsWrapper, inputOutlined } from './SalaryPage.sx';
import { usePagination } from 'app/utils/customHooks/usePagination';
import logger from 'app/utils/logger';

const SalaryPage = () => {
  const {
    filterBtns,
    tableHeader,
    details,
    anchorEl,
    activeAnchor,
    pageType,
    tabletScreen,
    smallScreen,
    pageSubpage,
    handleNewPaymentPage,
    handleChangePageType,
    handleClose,
  } = useSalaryPage();
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm();

  const { page: employeesPage, handleChange: setEmployeesPage } = usePagination();

  const { page: unEmployeesPage, handleChange: setUnEmployeesPage } = usePagination();

  const navigate = useNavigate();

  const [checkModal, setCheckModal] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState<ConfirmPaymentFormData>();
  const [totalAmount, setTotalAmount] = useState(0);
  const [uploadFileNameLoading, setUploadFileNameLoading] = useState(false);
  const [openFileTypeError, setOpenFileTypeError] = useState(false);

  const salaryAppy = useSendSalaryApply();

  const onSubmit = (data: SalaryApplyForm) => {
    salaryAppy.mutate(
      {
        com_acc_no: data.com_acc_no.value,
        description: data.description,
        db_acc_no: data.db_acc_no.value,
      },
      {
        onSuccess: (res) => {
          setPaymentDetails(res);
          navigate('/az/salary?type=payment-details');
        },
        onError: (error: any) => {
          if (error?.response?.data?.error?.fields?.db_acc_no) {
            setError('db_acc_no', { message: error?.response?.data?.error?.fields?.db_acc_no, type: 'validate' });
          }
          if (error?.response?.data?.error?.fields?.com_acc_no) {
            setError('com_acc_no', { message: error?.response?.data?.error?.fields?.com_acc_no, type: 'validate' });
          }
          if (error?.response?.data?.error?.fields?.description) {
            setError('description', { message: error?.response?.data?.error?.fields?.description, type: 'validate' });
          }
        },
      }
    );
  };

  const [searchInput, setSearchInput] = useState('');

  const handleChangeSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchSalaryData,
  } = useQuery('getSalaryData', salaryService.getSalaryPageData, {
    enabled: pageType === 'new-payments',
    onSuccess: (salaryData) => {
      setTotalAmount(salaryData?.payment_amount);
    },
  });

  const {
    data: workers,
    isLoading: workersLoading,
    refetchWorkers,
    fetchingWorkers,
  } = useGetSalaryWorkers(
    {
      page_id: pageSubpage === 'employee-deleted' ? unEmployeesPage : employeesPage,
      search: pageSubpage === 'employee-deleted' ? '' : searchInput,
      unlisted: pageSubpage === 'employee-deleted',
    },
    pageType === 'employee-table'
  );

  interface Label {
    value: string;
    label: string;
  }
  const [spendingValue, setSpendingValue] = useState(null);
  const [commissionValue, setCommissionValue] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = () => {
    if (data?.payment_list?.length > 0) {
      setCheckModal(true);

      return;
    }
    fileInputRef?.current?.click();
  };

  const handleFileChange = async (event: any) => {
    const acceptedFileTypes = ['xls', 'xlsx'];
    const file = event.target.files[0];

    const ext = file?.name?.split('.')?.pop()?.toLowerCase();

    if (!acceptedFileTypes.includes(ext)) {
      setOpenFileTypeError(true);

      return;
    }
    setUploadFileNameLoading(true);
    const response: any = await salaryService.uploadFile(file);

    if (response?.file_id) {
      const response2: any = await salaryService.uploadFileName(response?.file_id);

      if (response2) {
        refetchSalaryData();
        setUploadFileNameLoading(false);
      }
    }
  };

  const handleSpendingChange = (value: Label) => {
    setSpendingValue(value);
  };

  const handleCommissionChange = (value: Label) => {
    setCommissionValue(value);
  };

  const handleDownloadFile = () => {
    const link = document.createElement('a');
    link.href = data?.excel_template;

    link.download = 'salary_template';
    window?.document.body.appendChild(link);
    link.click();

    window?.document.body.removeChild(link);
  };

  const mutatedSelectValues = data?.account_list?.map((item) => ({
    value: item.iban,
    label: `${item.product_name} - ${item?.main_balance} ${useCurrencySymbol(item?.currency)}`,
  }));
  const includeInvalidIban = data?.payment_list?.some((row) => row.iban_error);

  return (
    <>
      <Snackbar
        open={openFileTypeError}
        autoHideDuration={3000}
        onClose={() => setOpenFileTypeError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          Ancaq excel file yükləyə bilərsiniz
        </Alert>
      </Snackbar>
      <input
        type="file"
        accept=".xls,.xlsx"
        ref={fileInputRef}
        style={{
          display: 'none',
        }}
        onChange={handleFileChange}
      />
      <PageHeader
        pageName={
          pageSubpage !== 'employee-deleted' && pageType !== 'payment-details'
            ? 'Əmək haqqı'
            : pageType === 'payment-details'
            ? 'Əməliyyatın təsdiqi'
            : 'Siyahıdan çıxardılmış işçilər'
        }
        finishedFile={pageType === 'new-payments'}
        sampleFileShow={pageType === 'new-payments'}
        showBtnBox={!tabletScreen || pageType === 'employee-table'}
        back={pageType === 'payment-details' || pageSubpage === 'employee-deleted'}
        employeesExit={pageType === 'employee-table' && pageSubpage !== 'employee-deleted'}
        searchBar={pageSubpage === 'employee-deleted'}
        handleFileUpload={handleFileUpload}
        handleDownloadFile={handleDownloadFile}
      />
      {pageType !== 'payment-details' && pageSubpage !== 'employee-deleted' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '8px', minWidth: '348px' }}>
            {filterBtns.map((item) => (
              <Button
                key={item.id}
                color={'tertiary'}
                sx={
                  pageType === item.route
                    ? { ...btnStatusPayment, background: grayFilter, boxShadow: '2px 2px 32px rgba(0, 0, 0, 0.06)' }
                    : btnStatusPayment
                }
                onClick={() => handleChangePageType(item.route)}
              >
                {item.title}
              </Button>
            ))}
          </Box>
          {pageType !== 'new-payments' && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              <SearchInput
                value={searchInput}
                onChange={handleChangeSearchInput}
                width={pageType === 'employee-table' && '387px'}
                placeholder={
                  pageType === 'employee-table' ? 'İşçinin adı, Hesab nömrəsi, Kartın bitmə tarixi' : 'Təyinat, məbləğ'
                }
              />
              {pageType !== 'employee-table' && (
                <Box sx={{ display: 'flex', gap: '4px', width: '296px' }}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    placeholderText="Tarixdən"
                    endDate={endDate}
                    customInput={
                      <OutlinedInput
                        sx={{
                          ...inputOutlined,
                          '& .MuiInputBase-input': {
                            padding: '12px 4px',
                          },
                        }}
                        startAdornment={
                          <InputAdornment sx={{ margin: '0px' }} position={'start'}>
                            <CalendarIcon sx={{ fill: 'transparent' }} />
                          </InputAdornment>
                        }
                      />
                    }
                  />
                  _
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    placeholderText="Tarixə"
                    customInput={
                      <OutlinedInput
                        sx={{
                          ...inputOutlined,
                          '& .MuiInputBase-input': {
                            padding: '12px 4px',
                          },
                        }}
                        startAdornment={
                          <InputAdornment sx={{ margin: '0px' }} position={'start'}>
                            <CalendarIcon sx={{ fill: 'transparent' }} />
                          </InputAdornment>
                        }
                      />
                    }
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      {pageType === 'new-payments' && (
        <form>
          {tabletScreen && (
            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                marginTop: '24px',
                width: smallScreen ? '170px' : 'auto',
                minWidth: '563px',
              }}
            >
              {' '}
              <Button sx={greenBtn} onClick={handleFileUpload}>
                <FileUploadIcon color={'secondary'} />
                <Typography sx={{ color: '#ffffff', fontWeight: '500', lineHeight: 1 }}>Hazır faylı yüklə</Typography>
              </Button>
              <Button onClick={handleDownloadFile} variant={'outlined'}>
                <DownloadAdocIcon />
                <Typography sx={{ fontWeight: '500', lineHeight: 1 }}>Nümunə fayla bax</Typography>
              </Button>
            </Box>
          )}

          <Box sx={inputsBox}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Ödəniş detalları</Typography>
            <Box sx={selectDetailsBox}>
              <Box sx={inputsWrapper}>
                <CustomSelect
                  value={spendingValue}
                  onChange={handleSpendingChange}
                  control={control}
                  options={mutatedSelectValues}
                  title={'Məxaric hesabı'}
                  placeholder={'Məxaric hesabı'}
                  rest={{
                    ...register('db_acc_no', {
                      required: 'Məxaric hesabı mütləqdir',
                    }),
                  }}
                  width={smallScreen ? '295px' : '320px'}
                  isValid={!!errors.db_acc_no}
                  errorText={String(errors.db_acc_no?.message)}
                />
                <CustomSelect
                  value={commissionValue}
                  onChange={handleCommissionChange}
                  options={mutatedSelectValues}
                  title={'Komissiyanın məxaric hesabı'}
                  placeholder={'Komissiyanın məxaric hesabıı'}
                  rest={{
                    ...register('com_acc_no', {
                      required: 'Komissiya hesabı mütləqdir',
                    }),
                  }}
                  control={control}
                  width={smallScreen ? '295px' : '320px'}
                  isValid={!!errors.com_acc_no}
                  errorText={String(errors.com_acc_no?.message)}
                />
              </Box>

              <CustomInput
                width={'100%'}
                title={'Ödənişin təyinatı'}
                placeholder={'Ödənişin təyinatı'}
                rest={{
                  ...register('description', {
                    required: 'Təyinat mütələqdir',
                  }),
                }}
                isValid={!!errors.description}
                errorText={String(errors.description?.message)}
              />
            </Box>
          </Box>
          {!isLoading && !isFetching && !uploadFileNameLoading && (
            <SalaryTable
              tableHeader={tableHeader}
              tableRow={data?.payment_list}
              totalAmount={totalAmount}
              setTotalAmount={setTotalAmount}
              details={details}
              anchorEl={anchorEl}
              activeAnchor={activeAnchor}
              handleClose={handleClose}
              refetchSalaryData={refetchSalaryData}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
            }}
          >
            {/* <Pagination
              sx={pagination}
              siblingCount={smallScreen ? -1 : 1}
              count={10}
              variant="outlined"
              shape="rounded"
            /> */}
            <Button
              size={'large'}
              color={'info'}
              variant={'contained'}
              sx={{
                marginLeft: 'auto',
              }}
              onClick={handleSubmit(onSubmit)}
              disabled={includeInvalidIban}
            >
              Davam et
            </Button>
          </Box>
        </form>
      )}
      {pageType === 'employee-table' && pageSubpage !== 'employee-deleted' && (
        <EmployeeList
          page={employeesPage}
          handlePageChange={setEmployeesPage}
          workers={workers}
          refetchWorkers={refetchWorkers}
          fetchingWorkers={fetchingWorkers}
          workersLoading={workersLoading}
          refetchSalaryData={refetchSalaryData}
          handleNewPaymentPage={handleNewPaymentPage}
        />
      )}
      {pageSubpage === 'employee-deleted' && (
        <UnEmployeeList
          page={unEmployeesPage}
          handlePageChange={setUnEmployeesPage}
          workers={workers}
          refetchWorkers={refetchWorkers}
          fetchingWorkers={fetchingWorkers}
          workersLoading={workersLoading}
        />
      )}
      {/* startDate: dayjs(startDate).format('YYYY-DD-MM'), endDate: dayjs(endDate).format('YYYY-DD-MM'), */}
      {pageType === 'payments-history' ? (
        <>
          <PaymentsHistory
            search={searchInput}
            startDate={startDate ? dayjs(startDate).format('YYYY-MM-DD') : ''}
            endDate={endDate ? dayjs(endDate).format('YYYY-MM-DD') : ''}
          />
        </>
      ) : pageType === 'payment-details' && paymentDetails ? (
        <PaymentDetails paymentDetails={paymentDetails} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '32px' }}>
          {/*<Button*/}
          {/*  sx={saveTemplateBtn}*/}
          {/*  color={'secondary'}*/}
          {/*  onClick={() => setSaveTemplate(true)}*/}
          {/*  disabled={amounts.length === 0}*/}
          {/*>*/}
          {/*  <BookmarkIcon color={amounts.length === 0 ? 'secondary' : 'warning'} />*/}
          {/*  Şablon kimi yadda saxla*/}
          {/*</Button>*/}
          {/*<SaveTemplate open={saveTemplate} close={setSaveTemplate} />*/}
        </Box>
      )}
      <CheckModal
        open={checkModal}
        close={setCheckModal}
        title="Yeni sənəd yüklədiyiniz zaman əvvəl daxil etdiyiniz işçi siyahısı sıfırlanacaq."
        approveAction={() => {
          fileInputRef?.current?.click();
          setCheckModal(false);
        }}
      />
    </>
  );
};

export default SalaryPage;
